import { logDev } from '../utils/LogDev';

// Parse Firebase config from environment variables
export const firebaseConfig = {
    apiUrl: import.meta.env.VITE_API_URL,
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// ---------------------------------------------------------------------------
// Lazy Firebase initialisation — the SDK is NOT downloaded at page load.
// It is fetched on first call to getAuth() below, which happens only when
// auth state changes are first subscribed to (i.e. after the UI is visible).
// ---------------------------------------------------------------------------

// Simple session flag stored in localStorage so we know whether to bother
// initialising Firebase on startup. Guest users never set this flag and
// therefore never download any Firebase SDK.
const SESSION_FLAG = 'argvoid_has_session';
export const hasSession = () => !!localStorage.getItem(SESSION_FLAG);
const setSession = () => localStorage.setItem(SESSION_FLAG, '1');
const clearSession = () => localStorage.removeItem(SESSION_FLAG);

import type { Auth, User } from 'firebase/auth';

let _auth: Auth | null = null;
let _authInitPromise: Promise<Auth> | null = null;

async function initAuth(): Promise<Auth> {
    if (_auth) return _auth;
    if (_authInitPromise) return _authInitPromise;

    _authInitPromise = (async () => {
        const [{ initializeApp }, { getAuth, GoogleAuthProvider: _ }] = await Promise.all([
            import('firebase/app'),
            import('firebase/auth'),
        ]);
        const app = initializeApp(firebaseConfig);
        _auth = getAuth(app);
        return _auth;
    })();

    return _authInitPromise;
}

/**
 * A synchronous reference that is null until Firebase has been lazily initialised.
 * Use `getAuthAsync()` to guarantee Firebase is ready.
 * Code that only needs to CHECK currentUser (guest detection) can safely read auth?.currentUser.
 */
export const auth = {
    get currentUser() { return _auth?.currentUser ?? null; },
    signOut() { return _auth?.signOut() ?? Promise.resolve(); },
} as unknown as Auth;

export async function getAuthAsync(): Promise<Auth> {
    return initAuth();
}

export async function loginWithGoogle() {
    logDev("[Auth] Starting Google Login flow...");
    try {
        const a = await initAuth();
        const { signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');
        logDev("[Auth] Calling signInWithPopup...");
        const result = await signInWithPopup(a, new GoogleAuthProvider());
        setSession();
        logDev("[Auth] signInWithPopup success:", result.user.uid);
        return result.user;
    } catch (error) {
        console.error("[Auth] Error in loginWithGoogle:", error);
        throw error;
    }
}

export async function loginWithEmail(email: string, password: string) {
    const a = await initAuth();
    const { signInWithEmailAndPassword } = await import('firebase/auth');
    const result = await signInWithEmailAndPassword(a, email, password);
    setSession();
    return result;
}

export async function signupWithEmail(email: string, password: string) {
    const a = await initAuth();
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    return createUserWithEmailAndPassword(a, email, password);
}

export async function updateUserPassword(oldPassword: string, newPassword: string) {
    const a = await initAuth();
    const { updatePassword, reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
    const user = a.currentUser;
    if (!user || !user.email) throw new Error("No authenticated user");

    const credential = EmailAuthProvider.credential(user.email, oldPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
}

export function logout() {
    clearSession();
    return _auth?.signOut() ?? Promise.resolve();
}

export function subscribeToAuthChanges(callback: (user: User | null) => void): () => void {
    // Start Firebase init, then wire the listener once ready.
    // Returns an unsubscribe function.
    let unsubscribe: (() => void) | null = null;
    initAuth().then(() => {
        import('firebase/auth').then(({ onAuthStateChanged }) => {
            unsubscribe = onAuthStateChanged(_auth!, (user) => {
                if (user) {
                    setSession();
                } else {
                    clearSession();
                }
                callback(user);
            });
        });
    });
    return () => { unsubscribe?.(); };
}
